/**
 * @description Payment Methods section for Customer Details page.
 * Lists stored cards with set-default and remove actions. "Add Card" opens AddCardModal.
 *
 * Key features:
 * - Displays stored cards: brand icon, masked number, expiry, default badge
 * - Set as default / Remove actions
 * - "Add Card" button opens HPF modal for PCI-compliant card capture
 * - Permission-gated: requires transactions.view
 *
 * @dependencies
 * - storedCardsService: API client for card CRUD
 * - AddCardModal: HPF-based card capture modal
 *
 * @notes
 * - Card data never touches our servers (HPF only)
 * - This component reads from existing PaymentVaultService endpoints
 */

import { useState, useEffect, useCallback } from 'react';
import { CreditCard, Star, Trash2, Plus, AlertCircle } from 'lucide-react';
import storedCardsService from '../../services/api/storedCardsService';
import AddCardModal from './AddCardModal';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';
import ConfirmActionModal from '../shared/ConfirmActionModal';

const CARD_BRAND_COLORS = {
  'visa': 'text-blue-600',
  'mastercard': 'text-orange-500',
  'amex': 'text-blue-700',
  'discover': 'text-orange-600'
};

export default function StoredCardsSection({ customerId, customerData }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // cardId being acted on
  const [removeConfirm, setRemoveConfirm] = useState({ open: false, cardId: null });

  const fetchCards = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await storedCardsService.getCustomerCards(customerId);
      setCards(response.cards || []);
    } catch (err) {
      setError('Failed to load payment methods');
      console.error('Failed to fetch stored cards:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleSetDefault = async (cardId) => {
    try {
      setActionLoading(cardId);
      await storedCardsService.setDefaultCard(cardId, customerId);
      await fetchCards();
    } catch (err) {
      console.error('Failed to set default card:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveCard = (cardId) => {
    setRemoveConfirm({ open: true, cardId });
  };

  const handleConfirmRemove = async () => {
    try {
      setActionLoading(removeConfirm.cardId);
      await storedCardsService.removeCard(removeConfirm.cardId);
      setRemoveConfirm({ open: false, cardId: null });
      await fetchCards();
    } catch (err) {
      console.error('Failed to remove card:', err);
      setRemoveConfirm({ open: false, cardId: null });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCardAdded = () => {
    setShowAddModal(false);
    fetchCards();
  };

  const formatExpiry = (month, year) => {
    if (!month || !year) return '';
    return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
  };

  const isExpired = (month, year) => {
    if (!month || !year) return false;
    const now = new Date();
    return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
  };

  return (
    <PermissionGate permission={PERMISSIONS.payments.view}>
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="w-6 h-6" />
          Payment Methods
        </h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Card
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
          <p>{error}</p>
        </div>
      ) : cards.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>No cards on file</p>
          <p className="text-sm mt-1">Add a card to enable recurring payments</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {cards.map((card) => {
            const expired = isExpired(card.expiryMonth, card.expiryYear);
            return (
              <div
                key={card.id}
                className={`flex items-center justify-between p-4 ${expired ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-center gap-4">
                  <CreditCard className={`w-5 h-5 ${CARD_BRAND_COLORS[card.cardBrand?.toLowerCase()] || 'text-gray-400'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {card.cardBrand || 'Card'} ending in {card.lastFour || '****'}
                      </span>
                      {card.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand">
                          <Star className="w-3 h-3" /> Default
                        </span>
                      )}
                      {expired && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Expired
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Expires {formatExpiry(card.expiryMonth, card.expiryYear)}
                      {card.fundingSource && ` · ${card.fundingSource}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!card.isDefault && (
                    <button
                      onClick={() => handleSetDefault(card.id)}
                      disabled={actionLoading === card.id}
                      className="text-sm text-gray-600 hover:text-brand px-3 py-1.5 rounded border border-gray-300 hover:border-brand transition-colors disabled:opacity-50"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveCard(card.id)}
                    disabled={actionLoading === card.id}
                    className="text-sm text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                    title="Remove card"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddCardModal
          customerId={customerId}
          customerData={customerData}
          onClose={() => setShowAddModal(false)}
          onCardAdded={handleCardAdded}
        />
      )}
      <ConfirmActionModal
        open={removeConfirm.open}
        onClose={() => setRemoveConfirm({ open: false, cardId: null })}
        onConfirm={handleConfirmRemove}
        title="Remove Card"
        message="Are you sure you want to remove this card? Any recurring plans using this card will need a new payment method."
        confirmText="Remove Card"
        variant="danger"
      />
    </div>
    </PermissionGate>
  );
}
